import {Component} from './component';

export class TagManager extends Component {

    setup() {
        this.addRemoveComponentEl = this.$refs.addRemove;
        this.container = this.$el;
        this.rowSelector = this.$opts.rowSelector;

        // Define datetime field names
        this.datetimeFields = ['valid_from', 'valid_to', 'expires_at', 'published_at', 'scheduled_at'];

        this.setupListeners();
        this.initializeExistingRows();
    }

    setupListeners() {
        this.container.addEventListener('input', event => {
            /** @var {AddRemoveRows} * */
            const addRemoveComponent = window.$components.firstOnElement(this.addRemoveComponentEl, 'add-remove-rows');
            if (!this.hasEmptyRows() && event.target.value) {
                addRemoveComponent.add();
            }

            // Check if input is a tag name field
            if (event.target.name && event.target.name.includes('[name]')) {
                this.handleNameFieldChange(event.target);
            }
        });

        // Add form submit listener to convert datetime fields to UTC
        const form = this.container.closest('form');
        if (form && !form.dataset.tagManagerListenerAdded) {
            form.addEventListener('submit', () => {
                this.convertDatetimeFieldsToUtc();
            });
            form.dataset.tagManagerListenerAdded = 'true';
        }
    }

    convertDatetimeFieldsToUtc() {
        // Find all datetime-local inputs in tag rows
        const rows = this.container.querySelectorAll(this.rowSelector);
        rows.forEach(row => {
            const valueInput = row.querySelector('input[name*="[value]"][type="datetime-local"]');
            if (valueInput && valueInput.value) {
                // Get UTC value
                const utcValue = valueInput.dataset.utcValue || this.localToUtc(valueInput.value);

                // Create hidden input with UTC value to submit
                const hidden = document.createElement('input');
                hidden.type = 'hidden';
                hidden.name = valueInput.name;
                hidden.value = utcValue;

                // Insert hidden input and remove name from datetime input (so it won't submit)
                valueInput.parentNode.insertBefore(hidden, valueInput);
                valueInput.removeAttribute('name');
            }
        });
    }

    initializeExistingRows() {
        // Initialize existing rows with datetime pickers if needed
        const rows = this.container.querySelectorAll(this.rowSelector);
        rows.forEach(row => {
            const nameInput = row.querySelector('input[name*="[name]"]');
            if (nameInput && nameInput.value) {
                this.updateValueInputType(nameInput);
            }
        });
    }

    handleNameFieldChange(nameInput) {
        this.updateValueInputType(nameInput);
    }

    updateValueInputType(nameInput) {
        const row = nameInput.closest('.card');
        if (!row) return;

        const valueInput = row.querySelector('input[name*="[value]"]');
        if (!valueInput) return;

        const tagName = nameInput.value.toLowerCase().trim();
        const isDatetimeField = this.datetimeFields.includes(tagName);

        if (isDatetimeField && valueInput.type !== 'datetime-local') {
            // Save original UTC value before changing type
            const originalValue = valueInput.value;

            // Clear value before changing type to avoid validation error
            valueInput.value = '';

            // Convert to datetime picker
            valueInput.type = 'datetime-local';
            valueInput.placeholder = 'YYYY-MM-DD HH:MM';

            // Store original UTC value and convert to local time for display
            if (originalValue) {
                console.log('[TagManager] Converting UTC to local:', originalValue);
                valueInput.dataset.utcValue = originalValue;
                // Convert UTC to local time for display
                const localValue = this.utcToLocal(originalValue);
                console.log('[TagManager] Local value:', localValue);
                valueInput.value = localValue;
            }

            // Add change listener to convert back to UTC when changed
            if (!valueInput.dataset.listenerAdded) {
                valueInput.addEventListener('change', (e) => {
                    if (e.target.value) {
                        // Convert local time to UTC and store
                        const utcValue = this.localToUtc(e.target.value);
                        e.target.dataset.utcValue = utcValue;
                    }
                });
                valueInput.dataset.listenerAdded = 'true';
            }
        } else if (!isDatetimeField && valueInput.type === 'datetime-local') {
            // Convert back to text - restore UTC value
            valueInput.type = 'text';
            valueInput.placeholder = valueInput.getAttribute('aria-label') || '';

            if (valueInput.dataset.utcValue) {
                valueInput.value = valueInput.dataset.utcValue;
                delete valueInput.dataset.utcValue;
            }
        }
    }

    /**
     * Convert UTC timestamp to local datetime-local format
     * Input: "2025-11-22T10:30:00.000000Z" or "2025-11-22T10:30:00Z" or "2025-11-22 10:30:00"
     * Output: "2025-11-22T12:30" (local time for datetime-local input)
     */
    utcToLocal(utcString) {
        try {
            // Normalize BookStack format (.000000Z) to JS format (.000Z)
            let normalizedString = utcString.replace(/\.\d{6}Z$/, '.000Z');

            // Parse UTC string
            let date;
            if (normalizedString.includes('Z') || normalizedString.includes('+')) {
                // ISO format with timezone
                date = new Date(normalizedString);
            } else {
                // Assume UTC without Z indicator
                date = new Date(normalizedString + 'Z');
            }

            if (isNaN(date.getTime())) {
                return utcString;
            }

            // Convert to local time in datetime-local format (YYYY-MM-DDTHH:mm)
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');

            return `${year}-${month}-${day}T${hours}:${minutes}`;
        } catch (e) {
            console.error('Error converting UTC to local:', e);
            return utcString;
        }
    }

    /**
     * Convert local datetime-local format to UTC timestamp
     * Input: "2025-11-22T12:30" (local time from datetime-local input)
     * Output: "2025-11-22T10:30:00.000000Z" (UTC with microseconds like BookStack)
     */
    localToUtc(localString) {
        try {
            // Parse as local time
            const date = new Date(localString);

            if (isNaN(date.getTime())) {
                return localString;
            }

            // Get ISO string and convert to BookStack format (6 digit microseconds)
            // From: "2025-11-22T10:30:00.123Z"
            // To:   "2025-11-22T10:30:00.000000Z"
            const isoString = date.toISOString();
            // Replace milliseconds (.123Z) with microseconds (.000000Z)
            return isoString.replace(/\.\d{3}Z$/, '.000000Z');
        } catch (e) {
            console.error('Error converting local to UTC:', e);
            return localString;
        }
    }

    hasEmptyRows() {
        const rows = this.container.querySelectorAll(this.rowSelector);
        const firstEmpty = [...rows].find(row => [...row.querySelectorAll('input')].filter(input => input.value).length === 0);
        return firstEmpty !== undefined;
    }

}
